import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { IndicatorState } from "src/app/indicator/indicator.state";
import { Util } from "src/app/shared/util";
import { GroupContainerComponent } from "./group-container/group-container.component";
import { LocationContainerComponent } from "./location-container/location-container.component";
import { PeriodContainerComponent } from "./period-container/period-container.component";
import { ViewSetContainerComponent } from "./viewset-container/viewset-container.component";
import { IndicatorType } from "../../indicator/constraints";

@Component({
  selector: "app-chart-viewer-filter",
  templateUrl: "./chart-viewer-filter.component.html",
  styleUrls: ["./chart-viewer-filter.component.css"],
})
export class ChartViewerFilterComponent implements OnInit {
  @ViewChild("viewContainerRef", { read: ViewContainerRef, static: true })
  VCR: ViewContainerRef;

  @ViewChild(LocationContainerComponent, { static: true })
  private locationComponent: LocationContainerComponent;

  @ViewChild(ViewSetContainerComponent, { static: true })
  private viewsetComponent: ViewSetContainerComponent;

  @ViewChild(PeriodContainerComponent, { static: true })
  private periodComponent: PeriodContainerComponent;

  child_unique_key = 0;
  componentsReferences = Array<ComponentRef<GroupContainerComponent>>();
  @Output() locationSubdivisionChanged: EventEmitter<string> =
    new EventEmitter();
  @Output() selectedLocationsChanged: EventEmitter<string[]> =
    new EventEmitter();
  @Output() selectedViewSetChanged: EventEmitter<string> =
    new EventEmitter();

  @Output() groupSelected: EventEmitter<string> = new EventEmitter();
  @Output() groupRemoved: EventEmitter<string> = new EventEmitter();
  @Output() groupItemsChanged: EventEmitter<{
    groupId: string;
    groupItems: string[];
  }> = new EventEmitter();
  @Output() periodItemsChanged: EventEmitter<{ periodItems: string[] }> =
    new EventEmitter();
  isPresum = false;
  hideViewsetContianer = false;
  closeViewsetMenu = false;
  closeLocationMenu = false;

  constructor() {}

  ngOnInit() {}

  onStateUpdated(state: IndicatorState) {
    if (this.locationComponent) this.locationComponent.onStateUpdated(state);
    if (this.viewsetComponent) this.viewsetComponent.onStateUpdated(state);
    this.periodComponent.onStateUpdated(state);
    this.isPresum = state.Meta?.typeId == IndicatorType.Presummarised;

    // for presum indicator, clear the view container if the viewset changes so that the order of the groups is up to date
    if (this.isPresum) {

      // if the state.Groups (other than the Location group) and the component references does not match, this means the viewset has changed
      const compGroups = this.componentsReferences.map(c => c.instance.groupId);
      const nonLocationGroups = state.Groups.filter(g => g !== state.Location);
      const hasMismatch =
        compGroups.length !== nonLocationGroups.length ||
        !compGroups.every(g => nonLocationGroups.includes(g)) ||
        !nonLocationGroups.every(g => compGroups.includes(g));
      // clear the view container and component references
      if (hasMismatch) {
        this.VCR.clear();
        this.componentsReferences = [];
      }

      this.hideViewsetContianer = this.viewsetComponent.viewsets.length <= 1;
    }

    // check if some group dropdowns must be removed
    this.componentsReferences.forEach((componentRef) => {
      if (!state.Groups.includes(componentRef.instance.groupId)) {
        const key = componentRef.instance.uniqueKey;
        for (let vcrIndex = 0; vcrIndex < this.VCR.length; vcrIndex++) {
          if (this.VCR.get(vcrIndex) == componentRef.hostView) {
            // removing component from container
            this.VCR.remove(vcrIndex);
          }
        }
        // removing component from the list
        this.componentsReferences = this.componentsReferences.filter(
          (x) => x.instance.uniqueKey !== key
        );
      }
    });

    // check if some expanded group dropdowns must be added
    if (state.Groups) {
      state.Groups.forEach((group) => {
        // for presum indicator, only show the group dropdown if the group is different than the location, as for presum indicators, the location is also considered a group but we don't want to show a dropdown for it
        if (this.isPresum && group == state.Location) return;

        if (group !== "Period") {
          let groupComponent = this.componentsReferences.find(
            (g) => g.instance.groupId === group
          );
          if (!groupComponent) {
            groupComponent = this.addNewGroupComponent();
            groupComponent.instance.groupId = group;
            groupComponent.instance.groupName = Util.getGroupLabel(
              group,
              state.Meta.groups
            );
          }
        }
      });
    }

    // more groups can be added
    if (state.AllowedGroups.length !== 0) {
      this.addNewGroupComponent();
    }

    this.componentsReferences.forEach((component) => {
      component.instance.onStateUpdated(state);
    });
  }

  addNewGroupComponent(): ComponentRef<GroupContainerComponent> {
    const childComponentRef = this.VCR.createComponent(GroupContainerComponent);
    const childComponent = childComponentRef.instance;

    childComponent.uniqueKey = ++this.child_unique_key;
    childComponent.parentRef = this;

    // add reference for newly created component
    this.componentsReferences.push(childComponentRef);
    return childComponentRef;
  }

  onlocationSubdivisionChanged(locationSubdivision: string) {
    this.locationSubdivisionChanged.emit(locationSubdivision);
  }

  onSelectedLocationsChanged(locations: string[]) {
    this.selectedLocationsChanged.emit(locations);
  }

  onGroupSelected(groupId: string) {
    this.groupSelected.emit(groupId);
  }

  onGroupRemoved(groupId: string) {
    this.groupRemoved.emit(groupId);
  }

  onGroupItemsChanged(groupId: string, groupItems: string[]) {
    this.groupItemsChanged.emit({ groupId, groupItems });
  }

  onPeriodItemsChanged(periodItems) {
    this.periodItemsChanged.emit(periodItems);
  }

  onSelectedViewSetChanged(viewset: string) {
    this.selectedViewSetChanged.emit(viewset);
  }

  onMenuOpened(isLocation: boolean) {
    if (!this.isPresum) return;

    // Close the viewset menu when location is opened and vice versa.
    // When one of them is opened, the other one should be closed to avoid overlapping of the dropdowns
    this.closeViewsetMenu = isLocation;
    this.closeLocationMenu = !isLocation;
  }
}
